import { assertNever } from "@impero-com/common/utils/obj";

import type {
    ControlConclusionAnswerKind,
    FieldConditionForControlConclusionAnswerKind,
} from "api/generated/model";

import { TasksFields } from "./types";

export function conditionalFieldShown(
    condition: FieldConditionForControlConclusionAnswerKind,
    targetValue: ControlConclusionAnswerKind["value"] | undefined
) {
    const compareConditionTargetValue = () => {
        switch (condition.value.type) {
            case "BlobIds":
            case "Effectiveness":
            case "Numeric":
            case "Option":
            case "Text": {
                switch (condition.operator) {
                    case "Equal":
                        return targetValue === condition.value.value;
                    case "NotEqual":
                        return targetValue !== condition.value.value;
                    default:
                        return assertNever(condition.operator);
                }
            }

            case "MultiOption": {
                if (targetValue === undefined) {
                    return false;
                }
                switch (condition.operator) {
                    case "Equal": {
                        const conditionValue = condition.value.value;
                        if (Array.isArray(targetValue)) {
                            return targetValue.some((value) =>
                                conditionValue.includes(value)
                            );
                        } else {
                            return false;
                        }
                    }
                    case "NotEqual": {
                        const conditionValue = condition.value.value;
                        if (Array.isArray(targetValue)) {
                            return targetValue.some(
                                (value) => !conditionValue.includes(value)
                            );
                        } else {
                            return false;
                        }
                    }
                    default:
                        return assertNever(condition.operator);
                }
            }
            default:
                return assertNever(condition.value);
        }
    };

    const matchesOperator = compareConditionTargetValue();

    switch (condition.action) {
        case "Show":
            return matchesOperator;
        case "Hide":
            return !matchesOperator;
        default:
            return assertNever(condition.action);
    }
}

export function getVisibleFields(
    fields: TasksFields,
    maybeGetFieldValue: (
        fieldId: string
    ) => ControlConclusionAnswerKind["value"] | undefined
) {
    return fields.filter((field) => {
        if (!field.condition || !field.condition.value) {
            return true;
        }
        return conditionalFieldShown(
            field.condition,
            maybeGetFieldValue(field.condition.fieldId)
        );
    });
}
